<script setup lang="ts">
/**
 * Renders the app-wide confirmDialog()/promptDialog() dialogs (useDialogs.ts).
 * Mounted ONCE in App.vue. Built on BaseModal so it stacks correctly above
 * any open modal, themes in dark mode, and closes on Escape/backdrop.
 */
import { ref, watch, nextTick } from 'vue';
import BaseModal from '@/components/common/BaseModal.vue';
import { dialogState } from '@/composables/useDialogs';

// --- confirm ---------------------------------------------------------------
function confirmResolve(ok: boolean) {
  dialogState.confirm?.resolve(ok);
  dialogState.confirm = null;
}

// --- prompt ----------------------------------------------------------------
const promptValue = ref('');
const promptInputRef = ref<HTMLInputElement | HTMLTextAreaElement | null>(null);

watch(
  () => dialogState.prompt,
  async (p) => {
    if (p) {
      promptValue.value = p.initial ?? '';
      await nextTick();
      promptInputRef.value?.focus();
    }
  },
);

function promptResolve(submit: boolean) {
  dialogState.prompt?.resolve(submit ? promptValue.value : null);
  dialogState.prompt = null;
}

function promptSubmit() {
  if (dialogState.prompt?.required && !promptValue.value.trim()) return;
  promptResolve(true);
}
</script>

<template>
  <!-- Confirm -->
  <BaseModal
    v-if="dialogState.confirm"
    :show="true"
    :title="dialogState.confirm.title"
    :title-icon="dialogState.confirm.danger ? 'bi-exclamation-triangle-fill text-danger' : 'bi-question-circle text-primary'"
    size="sm"
    @close="confirmResolve(false)"
  >
    <p class="mb-0" style="white-space: pre-line">{{ dialogState.confirm.message }}</p>

    <template #footer>
      <button type="button" class="btn btn-secondary" @click="confirmResolve(false)">
        {{ dialogState.confirm.cancelLabel || 'Cancel' }}
      </button>
      <button
        type="button"
        class="btn"
        :class="dialogState.confirm.danger ? 'btn-danger' : 'btn-primary'"
        @click="confirmResolve(true)"
      >
        <i v-if="dialogState.confirm.confirmIcon" :class="['bi', dialogState.confirm.confirmIcon, 'me-1']"></i>
        {{ dialogState.confirm.confirmLabel || 'Confirm' }}
      </button>
    </template>
  </BaseModal>

  <!-- Prompt -->
  <BaseModal
    v-if="dialogState.prompt"
    :show="true"
    :title="dialogState.prompt.title"
    title-icon="bi-pencil-square"
    size="sm"
    @close="promptResolve(false)"
  >
    <p v-if="dialogState.prompt.message" class="text-muted small" style="white-space: pre-line">
      {{ dialogState.prompt.message }}
    </p>
    <label v-if="dialogState.prompt.label" class="form-label" for="dialog-prompt-input">
      {{ dialogState.prompt.label }}
    </label>
    <textarea
      v-if="dialogState.prompt.multiline"
      id="dialog-prompt-input"
      ref="promptInputRef"
      v-model="promptValue"
      class="form-control"
      rows="3"
      :placeholder="dialogState.prompt.placeholder"
    ></textarea>
    <input
      v-else
      id="dialog-prompt-input"
      ref="promptInputRef"
      v-model="promptValue"
      type="text"
      class="form-control"
      :placeholder="dialogState.prompt.placeholder"
      @keydown.enter.prevent="promptSubmit"
    />

    <template #footer>
      <button type="button" class="btn btn-secondary" @click="promptResolve(false)">Cancel</button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!!dialogState.prompt.required && !promptValue.trim()"
        @click="promptSubmit"
      >
        {{ dialogState.prompt.confirmLabel || 'OK' }}
      </button>
    </template>
  </BaseModal>
</template>
